import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { getFollowerId } from "../lib/follows";
import { toast } from "sonner";
import { Calendar, CalendarCheck, Copy, AppleLogo, GoogleLogo, MicrosoftOutlookLogo, ArrowSquareOut } from "@phosphor-icons/react";
import { Button } from "./ui/button";

/**
 * CalendarSyncCard — opt-in toggle that lets a customer subscribe
 * their phone / Google / Outlook calendar to the events from every
 * truck they follow on TruckTrackr.
 *
 * One subscription URL per follower. When they toggle on, the
 * backend lazily mints a feed token and persists it. We then surface
 * one-tap subscribe links for the three major calendar surfaces and
 * a manual "copy URL" option for anyone else.
 *
 * The backend feed honors the `enabled` flag — when off, it returns
 * an empty (but valid) ICS so existing subscribers gracefully drain
 * to empty rather than getting a 404 / stale data.
 */
export default function CalendarSyncCard({ compact = false }) {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(!compact);

  const refresh = async () => {
    setLoading(true);
    try {
      const fid = getFollowerId();
      const { data } = await api.get(
        `/followers/me/calendar-sync?follower_id=${encodeURIComponent(fid)}`,
      );
      setState(data);
    } catch (e) {
      toast.error(formatApiError(e) || "Couldn't load calendar settings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const toggle = async () => {
    if (!state || busy) return;
    const next = !state.enabled;
    setBusy(true);
    setState({ ...state, enabled: next }); // optimistic
    try {
      const { data } = await api.post("/followers/me/calendar-sync", {
        follower_id: getFollowerId(),
        enabled: next,
      });
      setState(data);
      toast.success(next ? "Calendar sync enabled" : "Calendar sync paused");
    } catch (e) {
      setState({ ...state, enabled: !next }); // rollback
      toast.error(formatApiError(e) || "Couldn't update");
    } finally {
      setBusy(false);
    }
  };

  const ensureEnabled = async () => {
    if (state?.enabled) return state;
    try {
      const { data } = await api.post("/followers/me/calendar-sync", {
        follower_id: getFollowerId(),
        enabled: true,
      });
      setState(data);
      return data;
    } catch (e) {
      toast.error(formatApiError(e) || "Couldn't enable calendar sync");
      return null;
    }
  };

  // Subscribe-button click handlers — auto-enable the feed if it
  // isn't already, then navigate to the subscribe surface. We
  // INTERCEPT the anchor click so the auto-enable POST completes
  // before iOS opens the webcal:// system prompt (otherwise the
  // user's first subscription returns an empty calendar).
  const handleSubscribeClick = async (e, href) => {
    if (!state?.enabled) {
      e.preventDefault();
      const next = await ensureEnabled();
      if (next?.feed_https_url) {
        // After enable, re-derive the URL in case the token was minted
        const finalHref =
          href.startsWith("webcal://")
            ? next.feed_webcal_url
            : href.includes("calendar.google")
              ? `https://calendar.google.com/calendar/u/0/r?cid=${encodeURIComponent(next.feed_https_url)}`
              : href.includes("outlook.live")
                ? `https://outlook.live.com/calendar/0/addfromweb?url=${encodeURIComponent(next.feed_https_url)}&name=${encodeURIComponent("TruckTrackr — Followed Trucks")}`
                : href;
        window.location.assign(finalHref);
      }
    }
  };

  const copyUrl = async () => {
    let s = state;
    if (!s?.enabled) {
      s = await ensureEnabled();
      if (!s) return;
    }
    if (!s?.feed_https_url) return;
    try {
      await navigator.clipboard.writeText(s.feed_https_url);
      toast.success("Calendar URL copied");
    } catch {
      toast.error("Copy failed — please long-press the URL and copy manually");
    }
  };

  const googleAddUrl = state?.feed_https_url
    ? `https://calendar.google.com/calendar/u/0/r?cid=${encodeURIComponent(state.feed_https_url)}`
    : null;
  const outlookAddUrl = state?.feed_https_url
    ? `https://outlook.live.com/calendar/0/addfromweb?url=${encodeURIComponent(state.feed_https_url)}&name=${encodeURIComponent("TruckTrackr — Followed Trucks")}`
    : null;

  // Compact collapsed mode — render a single-row CTA. The customer
  // taps "Set up" to expand into the full panel. Used on the map
  // followed-trucks view so anonymous followers see the entry point
  // without making the map UI feel cluttered.
  if (compact && !expanded) {
    return (
      <section
        className="rounded-xl border border-border bg-card px-3 py-2 flex items-center gap-2"
        data-testid="calendar-sync-card-compact"
      >
        <Calendar weight="duotone" className="h-4 w-4 text-primary flex-none" />
        <div className="flex-1 min-w-0">
          <div className="text-xs font-semibold truncate">
            {state?.enabled ? "Calendar sync is on" : "Sync followed trucks to your calendar"}
          </div>
          <div className="text-[10.5px] text-muted-foreground truncate">
            {state?.enabled
              ? "Updates auto every ~8-24h. Tap to manage."
              : "Subscribe once. New events appear automatically."}
          </div>
        </div>
        <button
          type="button"
          onClick={() => setExpanded(true)}
          disabled={loading}
          className="flex-none rounded-full text-[11px] font-semibold px-3 py-1 bg-primary text-primary-foreground hover:bg-primary/90"
          data-testid="calendar-sync-compact-expand"
        >
          {state?.enabled ? "Manage" : "Set up"}
        </button>
      </section>
    );
  }

  return (
    <section
      className="rounded-2xl border border-border bg-card p-5"
      data-testid="calendar-sync-card"
    >
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h2 className="font-semibold text-lg flex items-center gap-2">
            <Calendar weight="duotone" className="h-5 w-5 text-primary" />
            Subscribe followed trucks to your calendar
          </h2>
          <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
            Tap a button below to subscribe. Every truck you follow will appear
            on your calendar automatically. Cancellations remove themselves.
            Works on iPhone, Android, Google Calendar, and Outlook.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="mt-2 h-24 bg-muted/40 rounded animate-pulse" />
      ) : (
        <div className="space-y-3" data-testid="calendar-sync-subscribe">
          {state?.enabled && (
            <p className="text-sm font-semibold flex items-center gap-1.5">
              <CalendarCheck weight="fill" className="h-4 w-4 text-emerald-500" />
              Your feed is live — pick where to subscribe:
            </p>
          )}

          {/* iPhone / Apple Calendar — webcal:// auto-opens the iOS
              subscription confirmation dialog. We auto-enable the
              feed if needed on click. */}
          <a
            href={state?.feed_webcal_url || "#"}
            onClick={(e) => handleSubscribeClick(e, state?.feed_webcal_url || "")}
            className="flex items-center justify-between gap-3 rounded-lg border border-border bg-card hover:bg-muted/40 transition px-3 py-2.5"
            data-testid="calendar-sync-apple"
          >
            <span className="flex items-center gap-2">
              <AppleLogo weight="duotone" className="h-5 w-5" />
              <span className="text-sm font-semibold">Apple Calendar (iPhone / Mac)</span>
            </span>
            <ArrowSquareOut weight="bold" className="h-4 w-4 text-muted-foreground" />
          </a>

          <a
            href={googleAddUrl || "#"}
            onClick={(e) => handleSubscribeClick(e, googleAddUrl || "")}
            target="_blank"
            rel="noreferrer"
            className="flex items-center justify-between gap-3 rounded-lg border border-border bg-card hover:bg-muted/40 transition px-3 py-2.5"
            data-testid="calendar-sync-google"
          >
            <span className="flex items-center gap-2">
              <GoogleLogo weight="duotone" className="h-5 w-5" />
              <span className="text-sm font-semibold">Google Calendar</span>
            </span>
            <ArrowSquareOut weight="bold" className="h-4 w-4 text-muted-foreground" />
          </a>

          <a
            href={outlookAddUrl || "#"}
            onClick={(e) => handleSubscribeClick(e, outlookAddUrl || "")}
            target="_blank"
            rel="noreferrer"
            className="flex items-center justify-between gap-3 rounded-lg border border-border bg-card hover:bg-muted/40 transition px-3 py-2.5"
            data-testid="calendar-sync-outlook"
          >
            <span className="flex items-center gap-2">
              <MicrosoftOutlookLogo weight="duotone" className="h-5 w-5" />
              <span className="text-sm font-semibold">Outlook</span>
            </span>
            <ArrowSquareOut weight="bold" className="h-4 w-4 text-muted-foreground" />
          </a>

          {/* Manual copy */}
          <div className="rounded-lg border border-dashed border-border bg-muted/30 px-3 py-2.5">
            <div className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground mb-1">
              Or copy the URL
            </div>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-[11px] truncate font-mono text-muted-foreground">
                {state?.enabled && state?.feed_https_url
                  ? state.feed_https_url
                  : "Tap any button above or Copy to generate your URL"}
              </code>
              <Button
                size="sm"
                variant="outline"
                onClick={copyUrl}
                data-testid="calendar-sync-copy"
              >
                <Copy weight="bold" className="h-3 w-3 mr-1" /> Copy
              </Button>
            </div>
          </div>

          <p className="text-[11px] text-muted-foreground leading-relaxed">
            <strong>Heads-up:</strong> iPhone calendar subscriptions typically
            refresh every ~24h; Google Calendar every ~8h. Same-day&nbsp;
            &quot;we&apos;re going live in an hour&quot; alerts continue to come from
            your push notifications, not the calendar feed.
          </p>

          {/* Pause / disable kill-switch — only visible AFTER the user
              has enabled sync at least once. Lets them pause the feed
              without unsubscribing from their phone calendar app. */}
          {state?.enabled && (
            <div className="pt-3 mt-2 border-t border-border flex items-center justify-between gap-2">
              <div className="text-xs text-muted-foreground">
                <strong className="text-foreground">Pause my feed</strong> — keeps your
                subscription URL, but the feed returns no events until you re-enable.
              </div>
              <button
                type="button"
                onClick={toggle}
                disabled={busy}
                aria-pressed={!!state?.enabled}
                aria-label="Pause calendar sync"
                data-testid="calendar-sync-toggle"
                className={`relative inline-flex h-6 w-11 flex-none items-center rounded-full transition ${
                  state?.enabled ? "bg-primary" : "bg-muted"
                } ${busy ? "opacity-50 cursor-wait" : ""}`}
              >
                <span
                  className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
                    state?.enabled ? "translate-x-5" : "translate-x-0.5"
                  }`}
                />
              </button>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
